import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  Grid,
  Avatar,
} from "@mui/material";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import { useFastUnlock } from "../../hooks/useFastUnlock";

const AdminPinPage = () => {
  const [storeCode, setStoreCode] = useState("");
  const [storeOptions, setStoreOptions] = useState([]);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("selectStore");
  const [error, setError] = useState("");
  const [employees, setEmployees] = useState([]);

  const { unlock } = useFastUnlock();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStoreLocations = async () => {
      try {
        const { data } = await api.get("/admin/stores");
        const options = data.map((loc) => ({
          label: loc.displayName,
          value: loc.name, // internal code
        }));
        setStoreOptions(options);
      } catch (err) {
        console.error("Failed to fetch store locations:", err);
      }
    };
    fetchStoreLocations();
  }, []);

  const handleVerifyPin = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.post("/admin/unlock", { pin, storeCode });
      if (res.status === 200) {
        const { data } = await api.get("/admin/employees/byStore", {
          params: { storeCode },
        });
        setEmployees(data);
        setStep("selectEmployee");
      }
    } catch (err) {
      const message =
        err?.response?.data?.error || "Incorrect PIN. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmployee = (employee) => {
    unlock({ storeCode, employee });
    navigate("/admin/dashboard");
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: 10,
          p: 4,
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: "#fff",
        }}>
        <Typography variant="h5" fontWeight="bold" align="center" gutterBottom>
          {step === "selectEmployee" ? "Who's Logging In?" : "Admin PIN Access"}
        </Typography>

        {step !== "selectEmployee" ? (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              mb={3}
              align="center">
              {step === "enterPin"
                ? "Enter the in-store access PIN to continue."
                : "Please select your store location."}
            </Typography>

            <Select
              fullWidth
              displayEmpty
              value={storeCode}
              onChange={(e) => {
                setStoreCode(e.target.value);
                setStep("enterPin");
              }}
              sx={{ mb: 3 }}>
              <MenuItem value="" disabled>
                Select Store Location
              </MenuItem>
              {storeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>

            <TextField
              fullWidth
              label="6-digit PIN"
              type="password"
              inputProps={{ maxLength: 6 }}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              disabled={!storeCode}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleVerifyPin();
              }}
              error={!!error}
              helperText={error}
              sx={{ mb: 3 }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyPin}
              disabled={loading || !pin || !storeCode}>
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Unlock"
              )}
            </Button>
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              mb={2}
              align="center">
              Select the staff member who's currently using the system.
            </Typography>
            <Grid container spacing={2}>
              {employees.map((emp) => (
                <Grid item xs={6} sm={4} key={emp._id}>
                  <Button
                    fullWidth
                    onClick={() => handleSelectEmployee(emp)}
                    sx={{
                      textTransform: "none",
                      py: 2,
                      border: "1px solid #eee",
                    }}>
                    <Box textAlign="center">
                      <Avatar sx={{ mx: "auto", mb: 1 }}>
                        {emp.firstName[0]}
                      </Avatar>
                      <Typography variant="body2" fontWeight="bold">
                        {emp.firstName}
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Container>
  );
};

export default AdminPinPage;
